import { useCallback, useRef } from 'react';

/**
 * Custom hook for auto-scrolling to newly revealed content
 * @param offset - Additional offset from the target element (default: 20)
 * @param behavior - Scroll behavior (default: 'smooth')
 */
export function useAutoScroll(offset: number = 20, behavior: ScrollBehavior = 'smooth') {
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Scrolls to bring an element into view with a slight delay
   * to allow for DOM updates and animations
   */
  const scrollToElement = useCallback((element: HTMLElement | null, delay: number = 100) => {
    if (!element) return;

    // Clear any pending scroll
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      const rect = element.getBoundingClientRect();
      const isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

      if (!isVisible) {
        element.scrollIntoView({ behavior, block: 'center' });
      }
    }, delay);
  }, [behavior]);

  /**
   * Scrolls to an element by its ref
   */
  const scrollToRef = useCallback((ref: React.RefObject<HTMLElement>, delay?: number) => {
    scrollToElement(ref.current, delay);
  }, [scrollToElement]);

  /**
   * Scrolls to an element by ID
   */
  const scrollToId = useCallback((id: string, delay?: number) => {
    const element = document.getElementById(id);
    scrollToElement(element, delay);
  }, [scrollToElement]);

  /**
   * Scrolls to the bottom of a container or the page
   */
  const scrollToBottom = useCallback((container?: HTMLElement | null, delay: number = 100) => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior });
      } else {
        window.scrollTo({ top: document.body.scrollHeight, behavior });
      }
    }, delay);
  }, [behavior]);

  return {
    scrollToElement,
    scrollToRef,
    scrollToId,
    scrollToBottom,
  };
}
